import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Alert,
    Tooltip,
    Button,
    CircularProgress,
    Snackbar,
} from "@mui/material";
import {
    Close as CloseIcon,
    Print as PrintIcon,
    Edit as EditIcon,
    LocalShipping as ShippingIcon,
    Sync as SyncIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    AttachMoney as MoneyIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Undo as UndoIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ContentCopy as ContentCopyIcon,
    TrackChanges as TrackIcon,
    Label as LabelIcon,
    CallSplit as ChannelIcon,
    CardGiftcard,
    Telegram as TelegramIcon,
    AssignmentReturn as ReturnIcon,
} from "@mui/icons-material";
import { Order, OrderItem, PaymentTransaction } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import { getCourierUrls, isSteadfastParcel } from "../../utils/courierUtils";
import { ParcelTrackingPanel } from "./ParcelTrackingPanel";
import { formatDateShort } from "../../utils/date";
import { AddPaymentDialog } from "./AddPaymentDialog";
import {
    RefundBkashPaymentDialog,
    RefundBkashPaymentRequest,
} from "./RefundBkashPaymentDialog";
import { ProcessReturnDialog } from "./ProcessReturnDialog";
import {
    orderReturnService,
    OrderReturn,
    CreateReturnRequest,
} from "../../services/orderReturnService";
import { orderService, AddPaymentRequest } from "../../services/orderService";
import { telegramService } from "../../services/telegramService";
import { AuditTrail } from "../common/AuditTrail";
import { usePermissions } from "../../hooks/usePermissions";
import { useAccounts } from "../../hooks/useAccounts";
import { ProductThumbnail } from "../common/ProductThumbnail";
import { getQuickEditRules } from "./quickEdit/quickEditRules";
import { OrderDeliveryPickers } from "./quickEdit/OrderDeliveryPickers";
import { OrderItemsEditor } from "./quickEdit/OrderItemsEditor";
import { OrderNoteEditor } from "./quickEdit/OrderNoteEditor";
import { getCustomerFullName } from "../../utils/customerUtils";
import { ChargeField, OrderChargesDialog } from "./quickEdit/OrderChargesDialog";
import { ShippingAddressDialog } from "./quickEdit/ShippingAddressDialog";
import { GiftsOnBillChip } from "./quickEdit/GiftsOnBillChip";
import { LinkedTasks } from "../../pages/tasks/LinkedTasks";

interface OrderDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    order: Order | null;
    onPrintReceipt?: (orderId: string, autoPrint?: boolean) => void;
    onUpdateStatus?: (order: Order) => void;
    onCreateCourier?: (order: Order) => void;
    onDuplicateOrder?: (order: Order) => void;
    /** Enables the quick edits (courier, channel, delivery charge, items);
     * receives the freshly re-read order after each one is saved. */
    onOrderChanged?: (order: Order) => void;
    onPaymentAdded?: (updatedOrder: Order) => void;
    onDiscarded?: (order: Order) => void;
    onNavigate?: (direction: "prev" | "next") => void;
    hasPrev?: boolean;
    hasNext?: boolean;
    currentIndex?: number;
    totalCount?: number;
}

const getStatusColor = (status: Order["status"]) => {
    switch (status) {
        case "DRAFT":
            return "default";
        case "PENDING":
            return "warning";
        case "CONFIRMED":
            return "info";
        case "SHIPPED":
            return "primary";
        case "DELIVERED":
            return "success";
        case "CANCELLED":
            return "error";
        default:
            return "default";
    }
};

const getPaymentStatusColor = (status: string) => {
    switch (status) {
        case "PAID":
            return "success";
        case "PARTIAL":
            return "warning";
        case "OVERPAID":
            return "info";
        case "REFUNDED":
            return "default";
        default:
            return "error";
    }
};

export const OrderDetailDrawer: React.FC<OrderDetailDrawerProps> = ({
    open,
    onClose,
    order,
    onPrintReceipt,
    onUpdateStatus,
    onCreateCourier,
    onDuplicateOrder,
    onOrderChanged,
    onPaymentAdded,
    onDiscarded,
    onNavigate,
    hasPrev = false,
    hasNext = false,
    currentIndex,
    totalCount,
}) => {
    const { canWrite, isOwner } = usePermissions();
    const { getAccountName } = useAccounts();
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [deletingTxId, setDeletingTxId] = useState<string | null>(null);
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [refundTarget, setRefundTarget] = useState<PaymentTransaction | null>(
        null,
    );
    const [returnDialogOpen, setReturnDialogOpen] = useState(false);
    const [orderReturns, setOrderReturns] = useState<OrderReturn[]>([]);
    const [sendingToCourier, setSendingToCourier] = useState(false);
    const [discardingDraft, setDiscardingDraft] = useState(false);
    const [localTransactions, setLocalTransactions] = useState<
        PaymentTransaction[] | null
    >(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        setLocalTransactions(null);
    }, [order?.order_id, open]);

    // Quick edits
    const [editingItems, setEditingItems] = useState(false);
    const [itemsDirty, setItemsDirty] = useState(false);
    const [noteDirty, setNoteDirty] = useState(false);
    const [addressEditOpen, setAddressEditOpen] = useState(false);
    const [chargeEdit, setChargeEdit] = useState<{ open: boolean; focus: ChargeField }>({
        open: false,
        focus: "charge",
    });

    // Another order, or a status change made elsewhere, ends item editing.
    useEffect(() => {
        setEditingItems(false);
        setItemsDirty(false);
    }, [order?.order_id, order?.status]);

    const notify = useCallback(
        (message: string, severity: "success" | "error" = "success") =>
            setSnackbar({ open: true, message, severity }),
        [],
    );

    /** Unsaved item or note changes ask before they're thrown away. */
    const confirmDiscardEdits = useCallback(() => {
        if (!itemsDirty && !noteDirty) return true;
        return window.confirm(
            itemsDirty && noteDirty
                ? "Discard your unsaved changes?"
                : itemsDirty
                  ? "Discard your item changes?"
                  : "Discard your note changes?",
        );
    }, [itemsDirty, noteDirty]);

    const requestClose = () => {
        if (confirmDiscardEdits()) onClose();
    };

    const requestNavigate = useCallback(
        (direction: "prev" | "next") => {
            if (onNavigate && confirmDiscardEdits()) onNavigate(direction);
        },
        [onNavigate, confirmDiscardEdits],
    );

    useEffect(() => {
        if (!open || !order?.order_id) {
            setOrderReturns([]);
            return;
        }
        orderReturnService
            .getReturns(order.order_id)
            .then(setOrderReturns)
            .catch(() => setOrderReturns([]));
    }, [order?.order_id, open]);

    useEffect(() => {
        if (!open || !onNavigate) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            // Arrow keys in a field (a quantity box, an amount) move the
            // cursor, and in a menu or dialog they belong to it — neither
            // should jump to another order.
            const target = e.target as HTMLElement | null;
            if (
                target &&
                (target.isContentEditable ||
                    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
                    target.closest('[role="menu"], [role="listbox"], .MuiDialog-root'))
            ) {
                return;
            }
            if (e.key === "ArrowLeft" && hasPrev) requestNavigate("prev");
            if (e.key === "ArrowRight" && hasNext) requestNavigate("next");
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onNavigate, hasPrev, hasNext, requestNavigate]);

    const transactions = useMemo(
        () => localTransactions ?? (order?.payment_transactions || []),
        [localTransactions, order],
    );

    const sumTransactions = (txs: PaymentTransaction[]) =>
        txs.reduce((s, t) => s + Number(t.amount), 0);

    const handleAddPayment = useCallback(
        async (data: AddPaymentRequest) => {
            if (!order) return;
            const currentOrder = order;
            const tx = await orderService.addPayment(
                currentOrder.order_id,
                data,
            );
            const newTransactions = [...transactions, tx];
            const newAmountPaid = newTransactions.reduce(
                (s, t) => s + Number(t.amount),
                0,
            );
            const total = Number(currentOrder.total_amount) || 0;
            const newStatus =
                newAmountPaid === 0
                    ? "UNPAID"
                    : newAmountPaid < total - 0.001
                      ? "PARTIAL"
                      : newAmountPaid <= total + 0.001
                        ? "PAID"
                        : "OVERPAID";
            setLocalTransactions(newTransactions);
            setSnackbar({
                open: true,
                message: "Payment added successfully.",
                severity: "success",
            });
            if (onPaymentAdded) {
                onPaymentAdded({
                    ...currentOrder,
                    payment_transactions: newTransactions,
                    amount_paid: newAmountPaid,
                    payment_status: newStatus as any,
                });
            }
        },
        [order, transactions, onPaymentAdded],
    );

    const handleDeletePayment = async (txId: string) => {
        if (!order) return;
        const currentOrder = order;
        setDeletingTxId(txId);
        try {
            await orderService.deletePayment(txId);
            const newTransactions = transactions.filter(
                (t) => t.transaction_id !== txId,
            );
            const newAmountPaid = newTransactions.reduce(
                (s, t) => s + Number(t.amount),
                0,
            );
            const total = Number(currentOrder.total_amount) || 0;
            const newStatus =
                newAmountPaid === 0
                    ? "UNPAID"
                    : newAmountPaid < total - 0.001
                      ? "PARTIAL"
                      : newAmountPaid <= total + 0.001
                        ? "PAID"
                        : "OVERPAID";
            setLocalTransactions(newTransactions);
            setSnackbar({
                open: true,
                message: "Payment deleted.",
                severity: "success",
            });
            if (onPaymentAdded) {
                onPaymentAdded({
                    ...currentOrder,
                    payment_transactions: newTransactions,
                    amount_paid: newAmountPaid,
                    payment_status: newStatus as any,
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Failed to delete payment.",
                severity: "error",
            });
        } finally {
            setDeletingTxId(null);
        }
    };

    const handleOpenRefund = (tx: PaymentTransaction) => {
        setRefundTarget(tx);
        setRefundDialogOpen(true);
    };

    const handleConfirmRefund = useCallback(
        async (data: RefundBkashPaymentRequest) => {
            if (!order || !refundTarget) return;
            const currentOrder = order;
            const result = await orderService.refundBkashPayment(
                currentOrder.order_id,
                refundTarget.transaction_id,
                data,
            );
            // Refresh from the authoritative source rather than
            // recomputing locally — payment_status can now be REFUNDED,
            // which the add/delete-payment handlers above don't know
            // about, and this also picks up the new refund row itself.
            const summary = await orderService.getPaymentSummary(
                currentOrder.order_id,
            );
            setLocalTransactions(summary.transactions);
            setSnackbar({
                open: true,
                message: result.pending
                    ? "Refund submitted — bKash hasn't confirmed it yet, we'll follow up. Use Recheck Status if it doesn't update shortly."
                    : result.message || "Refund completed.",
                severity: "success",
            });
            if (onPaymentAdded) {
                onPaymentAdded({
                    ...currentOrder,
                    payment_transactions: summary.transactions,
                    amount_paid: summary.amount_paid,
                    amount_due: summary.amount_due,
                    payment_status: summary.payment_status,
                });
            }
        },
        [order, refundTarget, onPaymentAdded],
    );

    const handleConfirmReturn = useCallback(
        async (data: CreateReturnRequest) => {
            if (!order) return;
            const currentOrder = order;
            await orderReturnService.createReturn(currentOrder.order_id, data);
            // Refresh the full order, not just the payment summary — a
            // return also decrements total_cost_price directly (see
            // OrderReturnService.create_return), which getPaymentSummary
            // doesn't carry, and this also picks up the new refund row (if
            // any) and the return record itself.
            const [freshOrder, returns] = await Promise.all([
                orderService.getOrderById(currentOrder.order_id),
                orderReturnService.getReturns(currentOrder.order_id),
            ]);
            setLocalTransactions(freshOrder.payment_transactions || []);
            setOrderReturns(returns);
            setSnackbar({
                open: true,
                message: "Return processed successfully.",
                severity: "success",
            });
            if (onPaymentAdded) {
                onPaymentAdded(freshOrder);
            }
        },
        [order, onPaymentAdded],
    );

    const handleRecheckRefundStatus = async (tx: PaymentTransaction) => {
        if (!order) return;
        const currentOrder = order;
        setDeletingTxId(tx.transaction_id); // reuse as a generic per-row busy flag
        try {
            const result = await orderService.getBkashRefundStatus(
                currentOrder.order_id,
                tx.transaction_id,
            );
            const summary = await orderService.getPaymentSummary(
                currentOrder.order_id,
            );
            setLocalTransactions(summary.transactions);
            setSnackbar({ open: true, message: result.message, severity: "success" });
            if (onPaymentAdded) {
                onPaymentAdded({
                    ...currentOrder,
                    payment_transactions: summary.transactions,
                    amount_paid: summary.amount_paid,
                    amount_due: summary.amount_due,
                    payment_status: summary.payment_status,
                });
            }
        } catch (err: any) {
            setSnackbar({
                open: true,
                message:
                    err?.response?.data?.detail || "Failed to recheck refund status.",
                severity: "error",
            });
        } finally {
            setDeletingTxId(null);
        }
    };

    if (!order) return null;

    const total = Number(order.total_amount) || 0;
    const amountPaidDisplay = sumTransactions(transactions);
    const amountDue = Math.max(
        0,
        Math.round((total - amountPaidDisplay) * 100) / 100,
    );
    // A fully-refunded order and one nobody ever paid for both sum to
    // zero — that's the one case the plain sum-vs-total math can't tell
    // apart on its own, so it needs an explicit check for a refund row.
    const hasRefund = transactions.some((t) => t.is_refund);
    const paymentStatusDisplay: string =
        amountPaidDisplay === 0
            ? hasRefund
                ? "REFUNDED"
                : "UNPAID"
            : amountPaidDisplay < total - 0.001
              ? "PARTIAL"
              : amountPaidDisplay <= total + 0.001
                ? "PAID"
                : "OVERPAID";

    // Calculate financials
    const items = order.items || [];
    const itemsRevenue = items.reduce(
        (sum, item) => sum + (item.total_price || 0),
        0,
    );
    const itemsCost = items.reduce((sum, item) => {
        const costPrice = item.unit_cost_price || 0;
        return sum + costPrice * item.quantity;
    }, 0);
    // Product COGS/Net Profit below must use the backend-authoritative
    // total_cost_price, not a fresh sum over items — a return reverses
    // cost for RESELLABLE lines directly on the order (see
    // OrderReturnService.create_return) without touching OrderItem, so
    // itemsCost alone would understate a return's effect on profit.
    // Falls back to itemsCost only for a legacy order missing the field.
    const totalCostPrice = order.total_cost_price ?? itemsCost;
    const discountAmount = order.discount_amount || 0;
    const shippingCharged = order.shipping_amount || 0;
    const deliveryCost = order.delivery_cost || 0;
    const totalRevenue = order.total_amount || 0;
    const netProfit = totalRevenue - totalCostPrice - deliveryCost;
    const profitMargin =
        totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    const quickEdit = canWrite && !!onOrderChanged && order.status !== "CANCELLED";
    const quickRules = getQuickEditRules(order, { isOwner, hasReturns: orderReturns.length > 0 });

    const handlePrint = () => {
        if (onPrintReceipt && order.order_id) {
            onPrintReceipt(order.order_id);
        }
    };

    const handleUpdateStatus = () => {
        if (onUpdateStatus) {
            onUpdateStatus(order);
        }
    };

    const handleCreateCourier = () => {
        if (onCreateCourier) {
            onCreateCourier(order);
        }
    };

    const handleDuplicateOrder = () => {
        if (onDuplicateOrder) {
            onDuplicateOrder(order);
        }
    };

    const handleSendToCourier = async () => {
        const serviceId = order.delivery_service?.delivery_service_id;
        if (!serviceId || !order.delivery_service?.tg_chat_id) return;
        setSendingToCourier(true);
        try {
            // The server looks up the courier's chat and sends it.
            await telegramService.sendOrderToDeliveryService(order, serviceId);
            setSnackbar({
                open: true,
                message: `Order sent to ${order.delivery_service?.name} on Telegram.`,
                severity: "success",
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message:
                    err instanceof Error && err.message
                        ? `Failed to send order to courier on Telegram. ${err.message}`
                        : "Failed to send order to courier on Telegram.",
                severity: "error",
            });
        } finally {
            setSendingToCourier(false);
        }
    };

    const handleDiscardDraft = async () => {
        if (
            !window.confirm(
                `Discard draft order #${order.order_number}? This can't be undone.`,
            )
        ) {
            return;
        }
        setDiscardingDraft(true);
        try {
            await orderService.deleteOrder(order.order_id);
            onDiscarded?.(order);
            onClose();
        } catch (err: any) {
            setSnackbar({
                open: true,
                message:
                    err.response?.data?.detail || "Failed to discard draft order.",
                severity: "error",
            });
        } finally {
            setDiscardingDraft(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={requestClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 700, md: 800 },
                        maxWidth: "100%",
                        mt: "64px", // Offset for top navigation bar (AppBar height)
                        height: "calc(100% - 64px)", // Adjust height to account for offset
                    },
                }}
                sx={{
                    zIndex: 1200, // Ensure drawer is above the nav bar (AppBar is typically 1100)
                }}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                        }}
                    >
                        {/* Left Column: Order Info */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="h2">
                                Order #{order.order_number}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mt: 0.5,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Chip
                                    label={order.status}
                                    color={getStatusColor(order.status) as any}
                                    size="small"
                                />
                                <Chip
                                    label={paymentStatusDisplay}
                                    color={
                                        getPaymentStatusColor(
                                            paymentStatusDisplay,
                                        ) as any
                                    }
                                    size="small"
                                    variant="outlined"
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    <CalendarIcon
                                        fontSize="inherit"
                                        sx={{
                                            mr: 0.5,
                                            verticalAlign: "middle",
                                        }}
                                    />
                                    {formatDateShort(order.order_date)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Right Column: Navigation + Actions */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.5,
                            }}
                        >
                            {/* Row 1: Navigation */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                {onNavigate && (
                                    <>
                                        <Tooltip title="Previous order (←)">
                                            <span>
                                                <IconButton
                                                    onClick={() =>
                                                        requestNavigate("prev")
                                                    }
                                                    disabled={!hasPrev}
                                                    size="small"
                                                    aria-label="Previous order"
                                                >
                                                    <ChevronLeftIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {currentIndex !== undefined &&
                                            totalCount !== undefined && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        minWidth: 36,
                                                        textAlign: "center",
                                                        userSelect: "none",
                                                    }}
                                                >
                                                    {currentIndex + 1}/
                                                    {totalCount}
                                                </Typography>
                                            )}
                                        <Tooltip title="Next order (→)">
                                            <span>
                                                <IconButton
                                                    onClick={() =>
                                                        requestNavigate("next")
                                                    }
                                                    disabled={!hasNext}
                                                    size="small"
                                                    aria-label="Next order"
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                                <IconButton onClick={requestClose} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Row 2: Action Buttons */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flexWrap: "wrap",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Tooltip title="View Receipt">
                                    <IconButton
                                        onClick={handlePrint}
                                        color="info"
                                        size="small"
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Print Receipt">
                                    <IconButton
                                        onClick={() => {
                                            if (onPrintReceipt && order.order_id) {
                                                onPrintReceipt(order.order_id, true);
                                            }
                                        }}
                                        color="primary"
                                        size="small"
                                    >
                                        <PrintIcon />
                                    </IconButton>
                                </Tooltip>
                                {canWrite && (
                                    <Tooltip title="Clone Order">
                                        <IconButton
                                            onClick={handleDuplicateOrder}
                                            color="info"
                                            size="small"
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canWrite && (
                                    <Tooltip title="Update Status">
                                        <IconButton
                                            onClick={handleUpdateStatus}
                                            color="primary"
                                            size="small"
                                        >
                                            <SyncIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canWrite &&
                                    order.status !== "DELIVERED" &&
                                    order.status !== "CANCELLED" && (
                                        <Tooltip title="Create Courier Order">
                                            <IconButton
                                                onClick={handleCreateCourier}
                                                color="warning"
                                                size="small"
                                            >
                                                <ShippingIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                {canWrite && order.delivery_service?.tg_chat_id && (
                                    <Tooltip
                                        title={`Send to ${order.delivery_service.name} on Telegram`}
                                    >
                                        <span>
                                            <IconButton
                                                onClick={handleSendToCourier}
                                                disabled={sendingToCourier}
                                                color="info"
                                                size="small"
                                            >
                                                <TelegramIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                {canWrite && order.status === "DRAFT" && (
                                    <Tooltip title="Discard Draft">
                                        <span>
                                            <IconButton
                                                onClick={handleDiscardDraft}
                                                disabled={discardingDraft}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
                        {/* Customer Information */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mb: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Customer & Shipping
                            </Typography>
                            {quickEdit ? (
                                <OrderDeliveryPickers
                                    order={order}
                                    lockedReason={quickRules.delivery}
                                    onOrderChanged={onOrderChanged!}
                                    notify={notify}
                                />
                            ) : (
                                <>
                                    {order.delivery_service?.name && (
                                        <Chip
                                            icon={<ShippingIcon fontSize="small" />}
                                            label={order.delivery_service.name}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {order.order_channel?.name && (
                                        <Chip
                                            icon={<ChannelIcon fontSize="small" />}
                                            label={order.order_channel.name}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                </>
                            )}
                            {items.some((i) => i.is_gift) && (
                                <GiftsOnBillChip
                                    order={order}
                                    editable={quickEdit}
                                    onOrderChanged={onOrderChanged!}
                                    notify={notify}
                                />
                            )}
                            {(() => {
                                const { trackingUrl, labelUrl } = getCourierUrls(order);
                                return (
                                    <>
                                        {trackingUrl && (
                                            <Tooltip title={`Track consignment ${order.consignment_id}`}>
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => window.open(trackingUrl, "_blank", "noopener")}
                                                >
                                                    <TrackIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {labelUrl && (
                                            <Tooltip title="Print shipping label">
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    onClick={() => window.open(labelUrl, "_blank", "noopener")}
                                                >
                                                    <LabelIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <PersonIcon
                                            color="action"
                                            fontSize="small"
                                        />
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                        >
                                            {order.customer
                                                ? getCustomerFullName(order.customer) || "Unnamed customer"
                                                : "Guest Customer"}
                                        </Typography>
                                    </Box>
                                    {order.customer?.phone && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <PhoneIcon
                                                color="action"
                                                fontSize="small"
                                            />
                                            <Typography variant="body2">
                                                {order.customer.phone}
                                            </Typography>
                                        </Box>
                                    )}
                                    {order.customer?.email && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ ml: 3 }}
                                        >
                                            {order.customer.email}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1,
                                        }}
                                    >
                                        <LocationIcon
                                            color="action"
                                            fontSize="small"
                                            sx={{ mt: 0.3 }}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                >
                                                    Shipping Address
                                                </Typography>
                                                {quickEdit && (
                                                    <Tooltip title="Edit shipping address">
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Edit shipping address"
                                                            onClick={() => setAddressEditOpen(true)}
                                                            sx={{ p: 0.25 }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {order.shipping_address ||
                                                    "No address provided"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                            <OrderNoteEditor
                                key={order.order_id}
                                order={order}
                                editable={quickEdit}
                                onOrderChanged={onOrderChanged!}
                                notify={notify}
                                onDirtyChange={setNoteDirty}
                            />
                        </Paper>

                        {/* Steadfast parcel steps, fetched on request */}
                        {isSteadfastParcel(order) && (
                            <ParcelTrackingPanel key={order.order_id} order={order} />
                        )}

                        {/* Items Table with Cost/Profit */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                {editingItems ? "Edit items" : "Items & Profitability"}
                            </Typography>
                            {quickEdit && !editingItems && (
                                <Tooltip title={quickRules.items ?? "Change quantities, remove, or add products and gifts"}>
                                    <span>
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon fontSize="small" />}
                                            onClick={() => setEditingItems(true)}
                                            disabled={!!quickRules.items}
                                        >
                                            Edit items
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                        {editingItems && quickEdit ? (
                            <OrderItemsEditor
                                key={order.order_id}
                                order={order}
                                paid={amountPaidDisplay}
                                onDone={() => setEditingItems(false)}
                                onDirtyChange={setItemsDirty}
                                onOrderChanged={onOrderChanged!}
                                notify={notify}
                            />
                        ) : (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ mb: 3 }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "grey.50" }}>
                                        <TableCell
                                            sx={{
                                                fontWeight: "bold",
                                                color: "primary.main",
                                            }}
                                        >
                                            Product
                                        </TableCell>
                                        <TableCell align="center">
                                            Qty
                                        </TableCell>
                                        <TableCell align="right">
                                            Unit Price
                                        </TableCell>
                                        <TableCell align="right">
                                            Unit Cost
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "primary.main",
                                            }}
                                        >
                                            Revenue
                                        </TableCell>
                                        <TableCell align="right">
                                            Cost
                                        </TableCell>
                                        <TableCell align="right">
                                            Profit
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item: OrderItem) => {
                                        const unitCost =
                                            item.unit_cost_price || 0;
                                        const itemCost =
                                            unitCost * item.quantity;
                                        const itemProfit =
                                            item.total_price - itemCost;
                                        const itemMargin =
                                            item.total_price > 0
                                                ? (itemProfit /
                                                      item.total_price) *
                                                  100
                                                : 0;

                                        return (
                                            <TableRow key={item.item_id}>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1.5,
                                                        }}
                                                    >
                                                        <ProductThumbnail
                                                            images={
                                                                item.product
                                                                    ?.images
                                                            }
                                                            alt={
                                                                item.product
                                                                    ?.name || ""
                                                            }
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                color="text.primary"
                                                            >
                                                                {item.product
                                                                    ?.name ||
                                                                    "Unknown Product"}
                                                            </Typography>
                                                            {item.is_gift && (
                                                                <Chip
                                                                    icon={
                                                                        <CardGiftcard fontSize="small" />
                                                                    }
                                                                    label="GIFT"
                                                                    size="small"
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {item.quantity}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {item.list_price &&
                                                        item.list_price >
                                                            item.unit_price && (
                                                            <Typography
                                                                variant="caption"
                                                                display="block"
                                                                sx={{
                                                                    textDecoration:
                                                                        "line-through",
                                                                    color: "text.disabled",
                                                                }}
                                                            >
                                                                {formatCurrencyBDT(
                                                                    item.list_price,
                                                                )}
                                                            </Typography>
                                                        )}
                                                    {formatCurrencyBDT(
                                                        item.unit_price,
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        color={
                                                            unitCost > 0
                                                                ? "text.secondary"
                                                                : "warning.main"
                                                        }
                                                    >
                                                        {unitCost > 0
                                                            ? formatCurrencyBDT(
                                                                  unitCost,
                                                              )
                                                            : "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color="primary.main"
                                                    >
                                                        {formatCurrencyBDT(
                                                            item.total_price,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography color="error.main">
                                                        -
                                                        {formatCurrencyBDT(
                                                            itemCost,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                        color={
                                                            itemProfit >= 0
                                                                ? "success.main"
                                                                : "error.main"
                                                        }
                                                    >
                                                        {formatCurrencyBDT(
                                                            itemProfit,
                                                        )}
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ ml: 0.5 }}
                                                        >
                                                            (
                                                            {itemMargin.toFixed(
                                                                1,
                                                            )}
                                                            %)
                                                        </Typography>
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        )}

                        {/* Financial Summary */}
                        <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                        >
                            Financial Summary
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2}>
                                {/* Revenue Section */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        gutterBottom
                                        display="block"
                                    >
                                        REVENUE
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Items Revenue
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatCurrencyBDT(itemsRevenue)}
                                        </Typography>
                                    </Box>
                                    {(discountAmount > 0 || quickEdit) && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                <Typography
                                                    variant="body2"
                                                    color="success.main"
                                                >
                                                    Discount
                                                </Typography>
                                                {quickEdit && (
                                                    <Tooltip title="Edit discount">
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Edit discount"
                                                            onClick={() => setChargeEdit({ open: true, focus: "discount" })}
                                                            sx={{ p: 0.25 }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                color="success.main"
                                            >
                                                -
                                                {formatCurrencyBDT(
                                                    discountAmount,
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                            <Typography variant="body2">
                                                Shipping Charged
                                            </Typography>
                                            {quickEdit && (
                                                <Tooltip title="Edit delivery charge">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit delivery charge"
                                                        onClick={() => setChargeEdit({ open: true, focus: "charge" })}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography variant="body2">
                                            {formatCurrencyBDT(shippingCharged)}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                        >
                                            Total Revenue
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            color="primary.main"
                                        >
                                            {formatCurrencyBDT(totalRevenue)}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Costs Section */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        gutterBottom
                                        display="block"
                                    >
                                        COSTS
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Product COGS
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="error.main"
                                        >
                                            -{formatCurrencyBDT(totalCostPrice)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mb: 0.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                            <Typography variant="body2">
                                                Delivery Cost
                                            </Typography>
                                            {quickEdit && (
                                                <Tooltip title="Edit delivery cost">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit delivery cost"
                                                        onClick={() => setChargeEdit({ open: true, focus: "cost" })}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="error.main"
                                        >
                                            -{formatCurrencyBDT(deliveryCost)}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                        >
                                            Net Profit
                                            {profitMargin > 0 && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ ml: 1 }}
                                                >
                                                    ({profitMargin.toFixed(1)}%
                                                    margin)
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                            color={
                                                netProfit >= 0
                                                    ? "success.main"
                                                    : "error.main"
                                            }
                                        >
                                            {netProfit >= 0 ? "+" : ""}
                                            {formatCurrencyBDT(netProfit)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Payment Information */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                Payment Details
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {canWrite && order.status === "DELIVERED" && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ReturnIcon />}
                                        onClick={() => setReturnDialogOpen(true)}
                                    >
                                        Return Items
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setAddPaymentOpen(true)}
                                    >
                                        Add Payment
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <MoneyIcon color="primary" />
                                <Box>
                                    {/* A div, not the default <p>: it holds a Chip (a div). */}
                                    <Typography
                                        variant="body1"
                                        component="div"
                                        fontWeight="medium"
                                    >
                                        Payment Status:{" "}
                                        <Chip
                                            label={paymentStatusDisplay}
                                            color={
                                                getPaymentStatusColor(
                                                    paymentStatusDisplay,
                                                ) as any
                                            }
                                            size="small"
                                            sx={{ ml: 0.5, fontWeight: "bold" }}
                                        />
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Amount Paid:{" "}
                                        {formatCurrencyBDT(amountPaidDisplay)} •
                                        Amount Due:{" "}
                                        {formatCurrencyBDT(amountDue)}
                                    </Typography>
                                </Box>
                            </Box>
                            {transactions.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Method</TableCell>
                                                <TableCell align="right">
                                                    Amount
                                                </TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Reference</TableCell>
                                                <TableCell>Account</TableCell>
                                                <TableCell align="center">
                                                    Action
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {transactions.map(
                                                (tx: PaymentTransaction) => (
                                                    <TableRow
                                                        key={tx.transaction_id}
                                                    >
                                                        <TableCell>
                                                            <Chip
                                                                label={
                                                                    tx.payment_method
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            {tx.is_refund && (
                                                                <Chip
                                                                    label="Refund"
                                                                    size="small"
                                                                    color="warning"
                                                                    sx={{ ml: 0.5 }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrencyBDT(
                                                                tx.amount,
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDateShort(
                                                                tx.payment_date,
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {tx.reference_number ||
                                                                "-"}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getAccountName(
                                                                tx.account_id,
                                                            ) || "—"}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {canWrite &&
                                                                tx.payment_method ===
                                                                    "BKASH" &&
                                                                !tx.is_refund &&
                                                                tx.bkash_payment_id && (
                                                                    <>
                                                                        <Tooltip title="Refund this payment via bKash">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="warning"
                                                                                    onClick={() =>
                                                                                        handleOpenRefund(
                                                                                            tx,
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        deletingTxId ===
                                                                                        tx.transaction_id
                                                                                    }
                                                                                >
                                                                                    <UndoIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                        <Tooltip title="Recheck bKash refund status">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        handleRecheckRefundStatus(
                                                                                            tx,
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        deletingTxId ===
                                                                                        tx.transaction_id
                                                                                    }
                                                                                >
                                                                                    {deletingTxId ===
                                                                                    tx.transaction_id ? (
                                                                                        <CircularProgress
                                                                                            size={
                                                                                                16
                                                                                            }
                                                                                        />
                                                                                    ) : (
                                                                                        <SyncIcon fontSize="small" />
                                                                                    )}
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                            {canWrite &&
                                                                !tx.bkash_payment_id && (
                                                                    <Tooltip title="Delete payment">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() =>
                                                                                    handleDeletePayment(
                                                                                        tx.transaction_id,
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    deletingTxId ===
                                                                                    tx.transaction_id
                                                                                }
                                                                            >
                                                                                {deletingTxId ===
                                                                                tx.transaction_id ? (
                                                                                    <CircularProgress
                                                                                        size={
                                                                                            16
                                                                                        }
                                                                                    />
                                                                                ) : (
                                                                                    <DeleteIcon fontSize="small" />
                                                                                )}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                )}
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    No payment transactions recorded for this
                                    order.
                                </Alert>
                            )}
                        </Paper>

                        {orderReturns.length > 0 && (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    Returns
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Return #</TableCell>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                    <TableCell align="right">
                                                        Revenue Reversed
                                                    </TableCell>
                                                    <TableCell align="right">Refund</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {orderReturns.map((ret) => (
                                                    <TableRow key={ret.return_id}>
                                                        <TableCell>{ret.return_number}</TableCell>
                                                        <TableCell>
                                                            {formatDateShort(ret.return_date)}
                                                        </TableCell>
                                                        <TableCell>{ret.reason}</TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrencyBDT(
                                                                ret.total_revenue_reversed,
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatCurrencyBDT(ret.refund_amount)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </>
                        )}

                        <LinkedTasks kind="order" id={order.order_id} label={order.order_number} />

                        <AuditTrail
                            createdByUserId={order.created_by_user_id}
                            updatedByUserId={order.updated_by_user_id}
                        />
                    </Box>
                </Box>
            </Drawer>

            <AddPaymentDialog
                open={addPaymentOpen}
                onClose={() => setAddPaymentOpen(false)}
                onConfirm={handleAddPayment}
                amountDue={amountDue}
            />

            <RefundBkashPaymentDialog
                open={refundDialogOpen}
                onClose={() => {
                    setRefundDialogOpen(false);
                    setRefundTarget(null);
                }}
                onConfirm={handleConfirmRefund}
                transaction={refundTarget}
            />

            {quickEdit && (
                <ShippingAddressDialog
                    open={addressEditOpen}
                    order={order}
                    onClose={() => setAddressEditOpen(false)}
                    onOrderChanged={onOrderChanged!}
                    notify={notify}
                />
            )}

            {quickEdit && (
                <OrderChargesDialog
                    open={chargeEdit.open}
                    focus={chargeEdit.focus}
                    order={order}
                    paid={amountPaidDisplay}
                    chargeLocked={quickRules.charge}
                    costLocked={quickRules.cost}
                    onClose={() => setChargeEdit((c) => ({ ...c, open: false }))}
                    onOrderChanged={onOrderChanged!}
                    notify={notify}
                />
            )}

            <ProcessReturnDialog
                open={returnDialogOpen}
                onClose={() => setReturnDialogOpen(false)}
                onConfirm={handleConfirmReturn}
                order={order}
                existingReturns={orderReturns}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                message={snackbar.message}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </>
    );
};
